<script setup lang="ts">
import { defaultTwitterCardMeta } from '@/constants/seo';
import { provideCreditSimulatorAnchor } from '~/composables/credit';
import { useAbsoluteUrl } from '~/composables/useAbsoluteUrl';

import {
	CreditComparison,
	CreditDisclaimer,
	CreditFaq,
	CreditFinalCta,
	CreditGoals,
	CreditHero,
	CreditHowItWorks,
	CreditMobileCta,
	CreditPartners,
	CreditRates,
	CreditWhyPilar
} from '~/components/CreditLanding';

definePageMeta({
	layout: 'default-transparent',
	path: '/financiamento'
});

const SEO_TITLE = 'PilarHomes Crédito';
const SEO_DESCRIPTION =
	'Simule financiamento, consórcio ou crédito com garantia de imóvel e receba propostas de vários bancos, comparadas por um especialista Pilar, sem custo.';

const { toAbsoluteUrl } = useAbsoluteUrl();
const ogImageUrl = toAbsoluteUrl('/images/social/openGraph/og_home.webp');

useSeoMeta({
	title: SEO_TITLE,
	description: SEO_DESCRIPTION,
	ogTitle: SEO_TITLE,
	ogDescription: SEO_DESCRIPTION,
	ogImage: ogImageUrl,
	twitterCard: defaultTwitterCardMeta,
	twitterTitle: SEO_TITLE,
	twitterDescription: SEO_DESCRIPTION,
	twitterImage: ogImageUrl
});

const route = useRoute();
const mixpanel = useMixpanel();

// Same page_viewed the other institutional pages send.
onMounted(() => {
	mixpanel.track('page_viewed', {
		current_page: route.fullPath,
		previous_page_url: document.referrer,
		event_name: 'page_viewed'
	});
});

const { card: simulatorCard } = provideCreditSimulatorAnchor();

// The mobile bar shows up while the simulator card is off screen. It starts
// hidden so the server and the client render the same thing.
const isSimulatorCardVisible = useElementVisibility(simulatorCard);
const isMounted = ref(false);
onMounted(() => {
	isMounted.value = true;
});
const isMobileCtaVisible = computed(
	() => isMounted.value && !isSimulatorCardVisible.value
);
</script>

<template>
	<div
		class="min-h-screen w-full bg-black pb-[76px] font-[Inter,system-ui,sans-serif] leading-[normal] text-[#FAFAFA] antialiased lg:pb-0 [&_:is(a,button,input)]:[font-family:inherit] [&_:is(a,button,input):focus-visible]:[outline:2px_solid_var(--color-beige)] [&_:is(a,button,input):focus-visible]:[outline-offset:2px]"
	>
		<CreditHero />
		<CreditPartners />
		<CreditGoals />
		<CreditHowItWorks />
		<CreditComparison />
		<CreditRates />
		<CreditWhyPilar />
		<CreditFaq />
		<CreditFinalCta />
		<CreditDisclaimer />
		<CreditMobileCta :is-visible="isMobileCtaVisible" />
	</div>
</template>
